import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { RootLayout } from './RootLayout';
import { ProtectedRoute, AuthCallback } from '../components/auth';
import { AppLoadingIndicator } from '../components/common/AppLoadingIndicator';
import { Spinner, Box } from '@cloudscape-design/components';

// Lazy load all view components for code splitting
const InteractView = lazy(() => import('../views/InteractView'));
const MyAgentsView = lazy(() => import('../views/agents/MyAgentsView'));
const SharedAgentsView = lazy(() => import('../views/agents/SharedAgentsView'));
const PublishedAgentsView = lazy(() => import('../views/agents/PublishedAgentsView'));
const SystemPromptEditorView = lazy(() => import('../views/agents/SystemPromptEditorView'));
const AnalyticsDashboardView = lazy(() => import('../views/analytics/AnalyticsDashboardView'));
const PhoneInventoryView = lazy(() => import('../views/telecom/PhoneInventoryView'));
const DialPlanView = lazy(() => import('../views/telecom/DialPlanView'));
const SIPServersView = lazy(() => import('../views/telecom/SIPServersView'));
const LocalToolsView = lazy(() => import('../views/tooling/LocalToolsView'));
const MCPServersView = lazy(() => import('../views/tooling/MCPServersView'));
const HistoryView = lazy(() => import('../views/HistoryView'));
const SettingsView = lazy(() => import('../views/SettingsView'));
const NotFoundView = lazy(() => import('../views/NotFoundView'));

// Loading component for initial app load
const InitialLoadingFallback = () => <AppLoadingIndicator />;

// Loading component for route transitions
const RouteLoadingFallback = () => (
  <Box textAlign="center" padding="xxl">
    <Spinner size="large" />
    <Box variant="p" color="text-body-secondary" margin={{ top: 's' }}>
      Loading...
    </Box>
  </Box>
);

// Wrapper component to add Suspense to lazy-loaded routes
const LazyRoute = ({ children, initial = false }: { children: React.ReactNode; initial?: boolean }) => (
  <Suspense fallback={initial ? <InitialLoadingFallback /> : <RouteLoadingFallback />}>
    {children}
  </Suspense>
);

export const router = createBrowserRouter([
  {
    path: '/auth/callback',
    element: <AuthCallback />,
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <RootLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <LazyRoute initial><InteractView /></LazyRoute>,
      },
      {
        path: 'agents',
        children: [
          {
            index: true,
            element: <Navigate to="/agents/my" replace />,
          },
          {
            path: 'my',
            element: <LazyRoute><MyAgentsView /></LazyRoute>,
          },
          {
            path: 'shared',
            element: <LazyRoute><SharedAgentsView /></LazyRoute>,
          },
          {
            path: 'published',
            element: <LazyRoute><PublishedAgentsView /></LazyRoute>,
          },
          {
            path: ':agentId/system-prompt',
            element: <LazyRoute><SystemPromptEditorView /></LazyRoute>,
          },
        ],
      },
      {
        path: 'telecom',
        children: [
          {
            index: true,
            element: <Navigate to="/telecom/phone-inventory" replace />,
          },
          {
            path: 'phone-inventory',
            element: <LazyRoute><PhoneInventoryView /></LazyRoute>,
          },
          {
            path: 'dial-plan',
            element: <LazyRoute><DialPlanView /></LazyRoute>,
          },
          {
            path: 'sip-servers',
            element: <LazyRoute><SIPServersView /></LazyRoute>,
          },
        ],
      },
      {
        path: 'tooling',
        children: [
          {
            index: true,
            element: <Navigate to="/tooling/local-tools" replace />,
          },
          {
            path: 'local-tools',
            element: <LazyRoute><LocalToolsView /></LazyRoute>,
          },
          {
            path: 'mcp-servers',
            element: <LazyRoute><MCPServersView /></LazyRoute>,
          },
        ],
      },
      {
        path: 'analytics',
        element: <LazyRoute><AnalyticsDashboardView /></LazyRoute>,
      },
      {
        path: 'history',
        element: <LazyRoute><HistoryView /></LazyRoute>,
      },
      {
        path: 'settings',
        element: <LazyRoute><SettingsView /></LazyRoute>,
      },
      {
        path: '*',
        element: <LazyRoute><NotFoundView /></LazyRoute>,
      },
    ],
  },
]);
